import { useCallback, useEffect, useSyncExternalStore } from 'react';

export interface CopyPosition {
    x: number;
    y: number;
}

interface CopyState {
    key: string | null;
    position: CopyPosition | null;
}

let state: CopyState = { key: null, position: null };
let timer: number | null = null;
let lastPointerPosition: CopyPosition | null = null;
let pointerListenerInstalled = false;
const listeners = new Set<() => void>();

function ensurePointerListener() {
    if (pointerListenerInstalled || typeof window === 'undefined') return;
    pointerListenerInstalled = true;
    const handler = (e: PointerEvent) => {
        lastPointerPosition = { x: e.clientX, y: e.clientY };
    };
    window.addEventListener('pointerdown', handler, { capture: true, passive: true });
    window.addEventListener('pointermove', handler, { capture: true, passive: true });
}

function subscribe(listener: () => void) {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

function getSnapshot() {
    return state;
}

function setState(next: CopyState) {
    state = next;
    listeners.forEach((listener) => listener());
}

/** Clears active copy feedback (e.g. when a sticky table scrolls and rows move). */
export function clearCopyFeedback() {
    if (timer !== null) {
        window.clearTimeout(timer);
        timer = null;
    }
    setState({ key: null, position: null });
}

async function copyToClipboard(
    text: string,
    key: string,
    timeoutMs: number,
    position?: CopyPosition | null,
): Promise<boolean> {
    try {
        await navigator.clipboard.writeText(text);
        setState({ key, position: position ?? lastPointerPosition });
        if (timer !== null) {
            window.clearTimeout(timer);
        }
        timer = window.setTimeout(() => {
            setState({ key: null, position: null });
            timer = null;
        }, timeoutMs);
        return true;
    } catch {
        return false;
    }
}

/**
 * Tracks a short-lived "just copied" state shared app-wide so only one
 * copy tooltip is active at a time. Pass a unique `key` per control.
 */
export function useCopyToClipboard(timeoutMs: number = 900) {
    const active = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

    useEffect(() => {
        ensurePointerListener();
    }, []);

    const copy = useCallback(
        (text: string, key: string = 'default', position?: CopyPosition | null) =>
            copyToClipboard(text, key, timeoutMs, position),
        [timeoutMs],
    );

    const isCopied = useCallback(
        (key: string = 'default') => active.key === key,
        [active.key],
    );

    return {
        copy,
        isCopied,
        copiedKey: active.key,
        copyPosition: active.position,
    };
}

export default useCopyToClipboard;
